import React, { useState } from "react";
import SkillBadge from "./SkillBadge";
import {
  Code,
  Database,
  Terminal,
  Cpu,
  Settings,
  Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Skill {
  name: string;
  level: number;
  category: string;
}

const Skills: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");

  const skills: Skill[] = [
    // Frontend
    { name: "HTML/CSS", level: 5, category: "frontend" },
    { name: "JavaScript", level: 5, category: "frontend" },
    { name: "TypeScript", level: 4, category: "frontend" },
    { name: "React", level: 5, category: "frontend" },
    { name: "Next.js", level: 5, category: "frontend" },
    { name: "React Native", level: 5, category: "frontend" },
    { name: "Expo", level: 5, category: "frontend" },
    { name: "TailwindCSS", level: 5, category: "frontend" },
    { name: "Storybook", level: 4, category: "frontend" },
    { name: "Vue.js", level: 3, category: "frontend" },
    { name: "Angular", level: 3, category: "frontend" },
    { name: "NuxtJS", level: 3, category: "frontend" },

    // Backend
    { name: "Laravel", level: 5, category: "backend" },
    { name: "Node.js", level: 4, category: "backend" },
    { name: "Express.js", level: 4, category: "backend" },
    { name: "REST API Design", level: 5, category: "backend" },
    { name: "WebSocket", level: 4, category: "backend" },
    { name: "Strapi CMS", level: 4, category: "backend" },

    // Database
    { name: "MySQL", level: 5, category: "database" },
    { name: "PostgreSQL", level: 4, category: "database" },
    { name: "SQL Optimization", level: 4, category: "database" },

    // Mobile
    { name: "React Native", level: 5, category: "mobile" },
    { name: "Expo", level: 5, category: "mobile" },
    { name: "Maestro", level: 4, category: "mobile" },
    { name: "Jest", level: 4, category: "mobile" },
    { name: "Mobile UI/UX", level: 4, category: "mobile" },
    { name: "App Store Deployment", level: 4, category: "mobile" },

    // DevOps
    { name: "Git", level: 5, category: "devops" },
    { name: "Docker", level: 4, category: "devops" },
    { name: "CI/CD", level: 4, category: "devops" },
    { name: "GitLab CI", level: 4, category: "devops" },
    { name: "GitHub Actions", level: 4, category: "devops" },

    // Testing
    { name: "Jest", level: 4, category: "testing" },
    { name: "Maestro", level: 4, category: "testing" },
  ];

  // Get icon based on category
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case "frontend":
        return <Code size={18} />;
      case "backend":
        return <Terminal size={18} />;
      case "database":
        return <Database size={18} />;
      case "mobile":
        return <Smartphone size={18} />;
      case "devops":
        return <Cpu size={18} />;
      case "testing":
        return <Settings size={18} />;
      default:
        return null;
    }
  };

  // Filter skills based on active tab
  const getFilteredSkills = () => {
    if (activeTab === "all") {
      return skills;
    }
    return skills.filter(
      (skill) => skill.category.toLowerCase() === activeTab.toLowerCase()
    );
  };

  const categories = [
    "all",
    "frontend",
    "backend",
    "mobile",
    "database",
    "devops",
    "testing",
  ];

  return (
    <section id="skills" className="py-20 bg-white">
      <div className="container-custom">
        <h2 className="section-title">Skills & Technologies</h2>

        <div className="mb-10">
          <Tabs defaultValue="all" onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 mb-8">
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="capitalize"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getFilteredSkills().map((skill) => (
                  <SkillBadge
                    key={skill.name}
                    name={skill.name}
                    level={skill.level}
                    category={skill.category}
                    icon={getCategoryIcon(skill.category)}
                  />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-16 bg-gray-50 rounded-lg p-6 border border-gray-100 shadow-sm">
          <h3 className="text-xl font-semibold text-portfolio-blue mb-4">
            Professional Development
          </h3>
          <p className="text-gray-700 mb-4">
            I'm committed to continuous learning and staying updated with the
            latest technologies and best practices in the industry. Here are
            some of my recent certifications and courses:
          </p>

          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <div className="bg-portfolio-blue/10 p-1.5 rounded-full mt-0.5">
                <div className="w-2 h-2 bg-portfolio-blue rounded-full"></div>
              </div>
              <span className="text-gray-700">
                Advanced React - Meta Certification (April 2025)
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <div className="bg-portfolio-blue/10 p-1.5 rounded-full mt-0.5">
                <div className="w-2 h-2 bg-portfolio-blue rounded-full"></div>
              </div>
              <span className="text-gray-700">
                Software Engineering Program - 1337 School
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <div className="bg-portfolio-blue/10 p-1.5 rounded-full mt-0.5">
                <div className="w-2 h-2 bg-portfolio-blue rounded-full"></div>
              </div>
              <span className="text-gray-700">
                React Native and Expo Mobile Development
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <div className="bg-portfolio-blue/10 p-1.5 rounded-full mt-0.5">
                <div className="w-2 h-2 bg-portfolio-blue rounded-full"></div>
              </div>
              <span className="text-gray-700">
                Laravel Full-Stack Development
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Skills;
